import { useState } from "react";

import { Button, type Column, EmptyState, Modal, Pill, Select, Table, Toggle } from "@/components/common";
import { useRetentionPolicies, useUpdateRetentionPolicy } from "@/hooks/useLgpd";
import type { RetentionAction, RetentionPolicy } from "@/types/lgpd";

const ACTION_LABEL: Record<string, string> = {
  anonymize: "Anonimizar",
  hard_delete: "Excluir",
  drop_partition: "Dropar partição",
};

const TRIGGER_LABEL: Record<string, string> = {
  created_at: "Criação",
  contract_end: "Fim do contrato",
  last_activity: "Última atividade",
  expires_at: "Expiração",
};

function years(days: number): string {
  if (days % 365 === 0) return `${days / 365} ano(s)`;
  return `${days} dias`;
}

export function RetentionPanel() {
  const { data: policies = [], isLoading } = useRetentionPolicies();
  const [selected, setSelected] = useState<RetentionPolicy | null>(null);

  const columns: Column<RetentionPolicy>[] = [
    {
      id: "resource",
      header: "Recurso",
      cell: (p) => <span className="cnpj">{p.resource_type}</span>,
    },
    { id: "trigger", header: "Gatilho", cell: (p) => <span className="td-muted">{TRIGGER_LABEL[p.trigger] ?? p.trigger}</span> },
    {
      id: "action",
      header: "Ação",
      cell: (p) => <Pill tone={p.action === "hard_delete" ? "alerta" : "diag"}>{ACTION_LABEL[p.action] ?? p.action}</Pill>,
    },
    { id: "duration", header: "Retenção", align: "right", cell: (p) => <span className="td-faded">{years(p.duration_days)}</span> },
    {
      id: "active",
      header: "Ativa",
      cell: (p) => <Pill tone={p.active ? "ativa" : "pausada"}>{p.active ? "Sim" : "Não"}</Pill>,
    },
  ];

  return (
    <>
      <Table
        rows={policies}
        columns={columns}
        getKey={(p) => String(p.id)}
        onRowClick={setSelected}
        empty={
          <EmptyState
            icon="layers"
            size="lg"
            title={isLoading ? "Carregando…" : "Nenhuma política"}
            description={isLoading ? undefined : "Políticas de retenção (Art. 16) aparecerão aqui."}
          />
        }
      />
      {selected && <PolicyModal policy={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function PolicyModal({ policy, onClose }: { policy: RetentionPolicy; onClose: () => void }) {
  const update = useUpdateRetentionPolicy();
  const [days, setDays] = useState(String(policy.duration_days));
  const [action, setAction] = useState<RetentionAction>(policy.action as RetentionAction);
  const [active, setActive] = useState(policy.active);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      await update.mutateAsync({
        id: policy.id,
        payload: { duration_days: Number(days), action, active },
      });
      onClose();
    } catch {
      setError("Não foi possível salvar. Obrigações legais exigem retenção mínima de 5 anos.");
    }
  }

  return (
    <Modal
      onClose={onClose}
      title={policy.resource_type}
      subtitle="Política de retenção (Art. 16)"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={update.isPending || !days.trim()}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="lgpd-detail">
        <label className="lgpd-field">
          <span>Duração (dias)</span>
          <input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} />
        </label>
        <label className="lgpd-field">
          <span>Ação</span>
          <Select<RetentionAction>
            value={action}
            onChange={setAction}
            options={[
              { value: "anonymize", label: "Anonimizar" },
              { value: "hard_delete", label: "Excluir" },
            ]}
          />
        </label>
        <Toggle checked={active} onChange={setActive} label="Política ativa" />
        {error && <p className="login-error">{error}</p>}
      </div>
    </Modal>
  );
}
